import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  Button,
  Modal,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Pagination,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { CSVLink } from 'react-csv';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { ProductContext } from '../../ProductProvider';
import BookArticlesForm from './BookArticlesForm';
import './ListBookArticles.css';

const config = require('../../config.json');

export default function ListBookArticles(props) {
  const { bookDetails = {}, handleParentClose } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { loggedUser } = useContext(ProductContext);

  const [articlesList, setArticlesList] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Search, Filter & Sort States
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('seq_asc');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Article Form Modal States
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [formTypeArticle, setFormTypeArticle] = useState('New');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [oldArticle, setOldArticle] = useState({});
  const [modalTitle, setModalTitle] = useState('');

  // Delete Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Citation Copy State
  const [copiedId, setCopiedId] = useState(null);

  const activeAccessionNo = bookDetails?.accessionNo || '';
  const activeBookTitle = bookDetails?.bookTitle || '';
  const activeCallNo = bookDetails?.bookClassificationNo || bookDetails?.callNo || '';

  // Fetch articles for this book accession number from API
  const fetchArticles = useCallback(async () => {
    if (!activeAccessionNo) {
      setArticlesList([]);
      setLoaded(true);
      return;
    }

    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || 'VJCollege';
      const cleanAccession = String(activeAccessionNo).trim().toUpperCase();

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books/articles`, {
        params: {
          fetchKey: cleanAccession,
          fetchOption: 'AccessionNo',
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      // Filter out only article records that belong to this book or have articleID/articleTitle
      const articles = data.filter(
        (item) => item.articleID || (item.articleTitle && String(item.accessionNo || '').toUpperCase() === cleanAccession)
      );

      setArticlesList(articles);
      setLoaded(true);
    } catch (error) {
      console.error('Error fetching book articles:', error);
      enqueueSnackbar(`Error fetching articles: ${error.message}`, { variant: 'warning' });
      setArticlesList([]);
      setLoaded(true);
    }
  }, [activeAccessionNo, enqueueSnackbar, loggedUser]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Open Article Form Modal (New, Edit, View)
  const handleOpenArticleModal = (event, type, item = null) => {
    if (event) event.preventDefault();
    setFormTypeArticle(type);

    if (type === 'New') {
      setSelectedArticle({
        clientID: loggedUser?.website || 'VJCollege',
        articleID: '',
        accessionNo: activeAccessionNo,
        bookTitle: activeBookTitle,
        articleTitle: '',
        articleSubTitle: '',
        articleAuthor: '',
        articleKeywords: '',
        callNo: activeCallNo,
        articleStatus: 'Active',
        articleDescription: '',
        startPageNo: '',
        endPageNo: '',
        updatedOn: '',
        updatedBy: loggedUser?.nickname || 'Librarian'
      });
      setOldArticle({});
      setModalTitle(`Catalogue New Article / Chapter for Book [${activeAccessionNo}]`);
    } else if (type === 'Edit') {
      setSelectedArticle(item);
      setOldArticle(item);
      setModalTitle(`Edit Article: ${item?.articleTitle || item?.articleID}`);
    } else if (type === 'View') {
      setSelectedArticle(item);
      setOldArticle({});
      setModalTitle(`Article Overview: ${item?.articleTitle || item?.articleID}`);
    }

    setShowArticleModal(true);
  };

  const handleCloseArticleModal = () => {
    setShowArticleModal(false);
    setSelectedArticle(null);
  };

  // Open Delete Confirmation Modal
  const handleDeleteClick = (event, item) => {
    if (event) event.preventDefault();
    setArticleToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setArticleToDelete(null);
  };

  // Delete Article Handler
  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;
    setIsDeleting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || 'VJCollege';

      const payload = {
        clientID: clientID,
        articleID: articleToDelete.articleID,
        accessionNo: articleToDelete.accessionNo || activeAccessionNo
      };

      await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/articles/delete`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Article "${articleToDelete.articleTitle || articleToDelete.articleID}" deleted successfully!`, {
        variant: 'success'
      });
      setShowDeleteModal(false);
      setArticleToDelete(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      enqueueSnackbar(`Error deleting article: ${error.response?.data?.message || error.message}`, {
        variant: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy Citation to Clipboard
  const handleCopyCitation = (event, item) => {
    if (event) event.stopPropagation();
    const authors = item.articleAuthor || 'Author Unknown';
    const title = item.articleTitle || 'Untitled';
    const book = item.bookTitle || activeBookTitle || 'Library Catalogue';
    const pages = item.startPageNo && item.endPageNo ? `pp. ${item.startPageNo}-${item.endPageNo}` : '';
    const citation = `"${title}." By ${authors}. In ${book} (${pages}). Accession No: ${item.accessionNo || activeAccessionNo}.`;

    navigator.clipboard.writeText(citation);
    setCopiedId(item.articleID);
    enqueueSnackbar('Citation copied to clipboard!', { variant: 'info' });
    setTimeout(() => setCopiedId(null), 3000);
  };

  // Unique Authors and Statuses for Filters
  const uniqueAuthors = useMemo(() => {
    const authors = new Set();
    articlesList.forEach((a) => {
      if (a.articleAuthor) {
        a.articleAuthor.split(',').forEach((name) => {
          const trimmed = name.trim();
          if (trimmed) authors.add(trimmed);
        });
      }
    });
    return Array.from(authors).sort();
  }, [articlesList]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    articlesList.forEach((a) => {
      if (a.articleStatus) statuses.add(a.articleStatus);
    });
    return Array.from(statuses).sort();
  }, [articlesList]);

  // KPI Metrics Calculation
  const kpiStats = useMemo(() => {
    const total = articlesList.length;
    const authorsCount = uniqueAuthors.length;

    let minPage = Infinity;
    let maxPage = -Infinity;
    articlesList.forEach((a) => {
      const s = parseInt(a.startPageNo, 10);
      const e = parseInt(a.endPageNo, 10);
      if (!isNaN(s) && s < minPage) minPage = s;
      if (!isNaN(e) && e > maxPage) maxPage = e;
    });

    const pageSpan =
      minPage !== Infinity && maxPage !== -Infinity ? `pp. ${minPage}–${maxPage}` : '—';

    return { total, authorsCount, pageSpan };
  }, [articlesList, uniqueAuthors]);

  // Filtered and Sorted Articles
  const filteredArticles = useMemo(() => {
    let result = [...articlesList];

    // Search Query Filter
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (a) =>
          (a.articleTitle || '').toLowerCase().includes(q) ||
          (a.articleSubTitle || '').toLowerCase().includes(q) ||
          (a.articleAuthor || '').toLowerCase().includes(q) ||
          (a.articleKeywords || '').toLowerCase().includes(q) ||
          (a.callNo || '').toLowerCase().includes(q) ||
          (a.articleID || '').toLowerCase().includes(q)
      );
    }

    // Author Filter
    if (authorFilter) {
      result = result.filter((a) =>
        (a.articleAuthor || '').toLowerCase().includes(authorFilter.toLowerCase())
      );
    }

    // Status Filter
    if (statusFilter) {
      result = result.filter((a) => (a.articleStatus || 'Active') === statusFilter);
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'seq_asc') {
        return (a.articleID || '').localeCompare(b.articleID || '', undefined, { numeric: true });
      }
      if (sortBy === 'seq_desc') {
        return (b.articleID || '').localeCompare(a.articleID || '', undefined, { numeric: true });
      }
      if (sortBy === 'page_asc') {
        const pA = parseInt(a.startPageNo, 10) || 0;
        const pB = parseInt(b.startPageNo, 10) || 0;
        return pA - pB;
      }
      if (sortBy === 'page_desc') {
        const pA = parseInt(a.startPageNo, 10) || 0;
        const pB = parseInt(b.startPageNo, 10) || 0;
        return pB - pA;
      }
      if (sortBy === 'title_asc') {
        return (a.articleTitle || '').localeCompare(b.articleTitle || '');
      }
      if (sortBy === 'title_desc') {
        return (b.articleTitle || '').localeCompare(a.articleTitle || '');
      }
      if (sortBy === 'author_asc') {
        return (a.articleAuthor || '').localeCompare(b.articleAuthor || '');
      }
      if (sortBy === 'newest') {
        return new Date(b.updatedOn || 0) - new Date(a.updatedOn || 0);
      }
      return 0;
    });

    return result;
  }, [articlesList, search, authorFilter, statusFilter, sortBy]);

  // Pagination Slice
  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / itemsPerPage));
  const paginatedArticles = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredArticles.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredArticles, currentPage, itemsPerPage]);

  // CSV Export Headers
  const csvHeaders = [
    { label: 'Article ID', key: 'articleID' },
    { label: 'Accession No', key: 'accessionNo' },
    { label: 'Book Title', key: 'bookTitle' },
    { label: 'Article Title', key: 'articleTitle' },
    { label: 'Subtitle', key: 'articleSubTitle' },
    { label: 'Author', key: 'articleAuthor' },
    { label: 'Call No', key: 'callNo' },
    { label: 'Status', key: 'articleStatus' },
    { label: 'Start Page', key: 'startPageNo' },
    { label: 'End Page', key: 'endPageNo' },
    { label: 'Keywords', key: 'articleKeywords' },
    { label: 'Abstract / Description', key: 'articleDescription' },
    { label: 'Updated On', key: 'updatedOn' },
    { label: 'Updated By', key: 'updatedBy' }
  ];

  return (
    <div className="list-book-articles-container">
      {/* 1. Context & Header Summary Card */}
      <div className="book-articles-header-banner">
        <div>
          <div className="book-articles-header-title">
            <i className="fa fa-book-bookmark"></i>
            Book Articles & Chapters Index
          </div>
          <div className="book-articles-header-subtitle">
            <span><strong>Book:</strong> {activeBookTitle || 'Catalogue Item'}</span>
            <span className="ms-2 badge bg-white bg-opacity-25 text-white font-monospace">
              Acc #{activeAccessionNo}
            </span>
            {activeCallNo && (
              <span className="ms-1 badge bg-white bg-opacity-10 text-white font-monospace">
                Call: {activeCallNo}
              </span>
            )}
          </div>
        </div>

        <div className="book-articles-header-actions">
          {filteredArticles.length > 0 && (
            <CSVLink
              data={filteredArticles}
              headers={csvHeaders}
              filename={`articles_${activeAccessionNo || 'book'}_${new Date().toISOString().slice(0, 10)}.csv`}
              className="btn-book-export-csv"
            >
              <i className="fa fa-file-csv text-success"></i> Export CSV
            </CSVLink>
          )}

          <Button
            variant="light"
            className="fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 text-primary"
            onClick={(e) => handleOpenArticleModal(e, 'New')}
          >
            <i className="fa fa-plus-circle text-primary"></i> Add New Article
          </Button>
        </div>
      </div>

      {/* 2. KPI Summary Cards */}
      <div className="book-articles-kpi-grid">
        <div className="book-article-kpi-card kpi-total">
          <div className="kpi-label">
            <i className="fa fa-file-lines text-indigo"></i> Total Articles
          </div>
          <div className="kpi-value">{kpiStats.total}</div>
          <div className="kpi-subtext">Catalogued for this book</div>
        </div>

        <div className="book-article-kpi-card kpi-authors">
          <div className="kpi-label">
            <i className="fa fa-user-pen text-success"></i> Authors
          </div>
          <div className="kpi-value">{kpiStats.authorsCount}</div>
          <div className="kpi-subtext">Contributing authors</div>
        </div>

        <div className="book-article-kpi-card kpi-pages">
          <div className="kpi-label">
            <i className="fa fa-file-invoice text-warning"></i> Page Span
          </div>
          <div className="kpi-value" style={{ fontSize: '1.25rem', paddingTop: '3px' }}>
            {kpiStats.pageSpan}
          </div>
          <div className="kpi-subtext">Covered book pages</div>
        </div>

        <div className="book-article-kpi-card kpi-call">
          <div className="kpi-label">
            <i className="fa fa-barcode text-purple"></i> Accession #
          </div>
          <div className="kpi-value font-monospace" style={{ fontSize: '1.2rem', paddingTop: '4px' }}>
            {activeAccessionNo || '—'}
          </div>
          <div className="kpi-subtext">{activeCallNo ? `Call No: ${activeCallNo}` : 'Dewey Call Number'}</div>
        </div>
      </div>

      {/* 3. Search & Filter Bar */}
      <div className="book-articles-toolbar-card">
        <Row className="g-3 align-items-center">
          <Col lg={4} md={6}>
            <div className="search-input-wrapper">
              <i className="fa fa-search search-input-icon"></i>
              <Form.Control
                type="text"
                placeholder="Search articles by title, author, keyword, or ID..."
                className="book-articles-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-sm text-muted position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent"
                  onClick={() => setSearch('')}
                >
                  <i className="fa fa-times-circle"></i>
                </button>
              )}
            </div>
          </Col>

          {uniqueAuthors.length > 1 && (
            <Col lg={2} sm={6}>
              <Form.Select
                className="filter-select"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
              >
                <option value="">All Authors ({uniqueAuthors.length})</option>
                {uniqueAuthors.map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}

          {uniqueStatuses.length > 1 && (
            <Col lg={2} sm={6}>
              <Form.Select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses ({uniqueStatuses.length})</option>
                {uniqueStatuses.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}

          <Col lg={uniqueAuthors.length > 1 || uniqueStatuses.length > 1 ? 2 : 4} sm={6}>
            <Form.Select
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="seq_asc">Sort: Article ID (1 &rarr; N)</option>
              <option value="seq_desc">Sort: Article ID (N &rarr; 1)</option>
              <option value="page_asc">Sort: Start Page (Low &rarr; High)</option>
              <option value="page_desc">Sort: Start Page (High &rarr; Low)</option>
              <option value="title_asc">Sort: Title (A &rarr; Z)</option>
              <option value="title_desc">Sort: Title (Z &rarr; A)</option>
              <option value="author_asc">Sort: Author (A &rarr; Z)</option>
              <option value="newest">Sort: Recently Updated</option>
            </Form.Select>
          </Col>

          <Col lg={2} sm={6} className="d-flex justify-content-end">
            <div className="view-toggle-group">
              <button
                type="button"
                className={`btn-view-toggle ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                <i className="fa fa-list"></i> Table
              </button>
              <button
                type="button"
                className={`btn-view-toggle ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Cards View"
              >
                <i className="fa fa-th-large"></i> Cards
              </button>
            </div>
          </Col>
        </Row>
      </div>

      {/* 4. Content Area */}
      {!loaded ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
          <div className="mt-3 text-muted fw-semibold">Loading book articles & chapters...</div>
        </div>
      ) : articlesList.length === 0 ? (
        /* Empty State */
        <div className="book-articles-empty-card">
          <div className="empty-state-icon">
            <i className="fa fa-book-open-reader"></i>
          </div>
          <div className="empty-state-title">No Articles / Chapters Registered Yet</div>
          <div className="empty-state-desc">
            No articles or chapters have been catalogued for book accession number &quot;{activeAccessionNo}&quot;.
            Click below to catalogue the first article with auto-generated sequential ID.
          </div>
          <Button
            variant="primary"
            size="lg"
            className="px-4 py-2 fw-semibold shadow-sm"
            onClick={(e) => handleOpenArticleModal(e, 'New')}
          >
            <i className="fa fa-plus-circle me-2"></i> Add First Article
          </Button>
        </div>
      ) : filteredArticles.length === 0 ? (
        /* No Match State */
        <div className="book-articles-empty-card">
          <div className="empty-state-icon text-secondary" style={{ background: '#f1f5f9' }}>
            <i className="fa fa-filter"></i>
          </div>
          <div className="empty-state-title">No Articles Found</div>
          <div className="empty-state-desc">
            No articles matched your search query &quot;{search}&quot; or filter criteria.
          </div>
          <Button
            variant="outline-primary"
            onClick={() => {
              setSearch('');
              setAuthorFilter('');
              setStatusFilter('');
            }}
          >
            <i className="fa fa-redo me-1"></i> Clear Filters
          </Button>
        </div>
      ) : viewMode === 'table' ? (
        /* ================= TABLE VIEW ================= */
        <div className="book-articles-table-wrapper">
          <Table hover responsive className="book-articles-table">
            <thead>
              <tr>
                <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                <th style={{ width: '130px' }}>Article ID</th>
                <th>Article Title & Subtitle</th>
                <th>Author(s)</th>
                <th>Pages</th>
                <th>Status</th>
                <th>Keywords</th>
                <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedArticles.map((item, index) => {
                const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                const keywords = (item.articleKeywords || '')
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k.length > 0);

                return (
                  <tr key={item.articleID || index}>
                    <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>
                      {globalIndex}
                    </td>

                    <td>
                      <span className="article-id-pill" title={item.articleID}>
                        <i className="fa fa-fingerprint me-1"></i>
                        {item.articleID}
                      </span>
                    </td>

                    <td className="article-title-cell">
                      <div className="article-main-title">{item.articleTitle}</div>
                      {item.articleSubTitle && (
                        <div className="article-sub-title">{item.articleSubTitle}</div>
                      )}
                    </td>

                    <td>
                      <span className="author-chip">
                        <i className="fa fa-user-edit text-primary"></i>
                        {item.articleAuthor || '—'}
                      </span>
                    </td>

                    <td>
                      {item.startPageNo && item.endPageNo ? (
                        <span
                          className="page-range-pill"
                          title={`${parseInt(item.endPageNo, 10) - parseInt(item.startPageNo, 10) + 1} pages total`}
                        >
                          <i className="fa fa-file-lines"></i> pp. {item.startPageNo}–{item.endPageNo}
                        </span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    <td>
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                        {item.articleStatus || 'Active'}
                      </span>
                    </td>

                    <td>
                      {keywords.length > 0 ? (
                        <div className="table-keyword-tags">
                          {keywords.slice(0, 3).map((kw, ki) => (
                            <span key={ki} className="table-tag">
                              #{kw}
                            </span>
                          ))}
                          {keywords.length > 3 && (
                            <span className="table-tag text-primary">+{keywords.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <Dropdown as={ButtonGroup} align="end">
                        <Dropdown.Toggle
                          as={Button}
                          variant="link"
                          className="p-0 border-0 text-muted shadow-none"
                          style={{ width: '32px', height: '32px' }}
                        >
                          <i className="fa fa-ellipsis-v text-primary" style={{ fontSize: '1.1rem' }}></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="shadow-lg border-0 rounded-3 p-2">
                          <Dropdown.Item
                            onClick={(e) => handleOpenArticleModal(e, 'View', item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2"
                          >
                            <i className="fa fa-eye text-primary"></i> View Details
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={(e) => handleOpenArticleModal(e, 'Edit', item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2"
                          >
                            <i className="fa fa-pen text-warning"></i> Edit Article
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={(e) => handleCopyCitation(e, item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2"
                          >
                            <i
                              className={
                                copiedId === item.articleID
                                  ? 'fa fa-check text-success'
                                  : 'fa fa-quote-right text-info'
                              }
                            ></i>
                            {copiedId === item.articleID ? 'Copied!' : 'Copy Citation'}
                          </Dropdown.Item>

                          <Dropdown.Divider />
                          <Dropdown.Item
                            onClick={(e) => handleDeleteClick(e, item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2 text-danger"
                          >
                            <i className="fa fa-trash text-danger"></i> Delete Article
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        /* ================= CARDS VIEW ================= */
        <div className="book-articles-card-grid">
          {paginatedArticles.map((item, index) => {
            const keywords = (item.articleKeywords || '')
              .split(',')
              .map((k) => k.trim())
              .filter((k) => k.length > 0);

            return (
              <div key={item.articleID || index} className="book-article-card-item">
                <div>
                  <div className="article-card-header">
                    <span className="article-id-pill">
                      <i className="fa fa-fingerprint me-1"></i>
                      {item.articleID}
                    </span>
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      {item.articleStatus || 'Active'}
                    </span>
                  </div>

                  <div className="article-card-body">
                    <h6 className="fw-bold text-dark mb-1">{item.articleTitle}</h6>
                    {item.articleSubTitle && (
                      <div className="small text-muted fst-italic mb-2">{item.articleSubTitle}</div>
                    )}
                    <div className="small text-secondary mb-2">
                      <i className="fa fa-user-edit text-primary me-1"></i>
                      <strong>Author:</strong> {item.articleAuthor || '—'}
                    </div>

                    {item.startPageNo && item.endPageNo && (
                      <div className="mb-2">
                        <span className="page-range-pill">
                          <i className="fa fa-file-lines me-1"></i> Pages {item.startPageNo}–{item.endPageNo}
                        </span>
                      </div>
                    )}

                    {keywords.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {keywords.map((kw, ki) => (
                          <span key={ki} className="table-tag">
                            #{kw}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="article-card-footer">
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={(e) => handleOpenArticleModal(e, 'View', item)}
                      title="View Article Overview"
                    >
                      <i className="fa fa-eye"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-warning"
                      onClick={(e) => handleOpenArticleModal(e, 'Edit', item)}
                      title="Edit Article"
                    >
                      <i className="fa fa-pen"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={(e) => handleDeleteClick(e, item)}
                      title="Delete Article"
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="link"
                    className="text-muted p-0 text-decoration-none"
                    onClick={(e) => handleCopyCitation(e, item)}
                  >
                    <i className="fa fa-quote-right me-1"></i> Cite
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. Pagination Bar */}
      {totalPages > 1 && (
        <div className="book-articles-pagination-bar">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Rows per page:</span>
            <Form.Select
              size="sm"
              style={{ width: '80px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>
            <span className="small text-muted ms-2">
              Showing {(currentPage - 1) * itemsPerPage + 1}–
              {Math.min(currentPage * itemsPerPage, filteredArticles.length)} of {filteredArticles.length} articles
            </span>
          </div>

          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                pageNum = currentPage - 3 + i;
                if (pageNum > totalPages) pageNum = totalPages - (4 - i);
              }
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            <Pagination.Next
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* ================= MODAL: ADD / EDIT / VIEW ARTICLE ================= */}
      <Modal show={showArticleModal} onHide={handleCloseArticleModal} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 text-dark">
            <i className="fa fa-file-lines text-primary me-2"></i>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-2">
          <BookArticlesForm
            handleClose={handleCloseArticleModal}
            oldArticle={oldArticle}
            formType={formTypeArticle}
            viewOnly={formTypeArticle === 'View'}
            bookDetails={bookDetails}
            articleData={selectedArticle}
            setArticleData={setSelectedArticle}
            existingArticles={articlesList}
            onSuccess={() => {
              fetchArticles();
            }}
          />
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: DELETE CONFIRMATION ================= */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 text-danger d-flex align-items-center gap-2">
            <i className="fa fa-triangle-exclamation"></i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-3">
          <p className="text-secondary mb-3">
            Are you sure you want to delete this article? This action cannot be undone.
          </p>
          {articleToDelete && (
            <div className="p-3 bg-light rounded-3 border">
              <div className="fw-bold text-dark mb-1">{articleToDelete.articleTitle || 'Untitled'}</div>
              {articleToDelete.articleSubTitle && (
                <div className="text-muted small fst-italic mb-2">{articleToDelete.articleSubTitle}</div>
              )}
              <div className="d-flex flex-wrap gap-2 small text-secondary">
                <span>
                  <strong>ID:</strong> <code className="text-dark">{articleToDelete.articleID}</code>
                </span>
                <span>
                  <strong>Author:</strong> {articleToDelete.articleAuthor || '—'}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            className="d-flex align-items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fa fa-trash"></i>
                Delete Article
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
